import axios from "axios";
import * as Sentry from "@sentry/browser";
import { requestFcmToken, onForegroundMessage } from "../../utils/firebase.js";
import { showAlert } from "../../utils/alert.js";

// =========================================================================
// GLOBAL AXIOS CONFIGURATION & INTERCEPTORS
// =========================================================================
window.axios = axios;
window.axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

const bootstrapToken = localStorage.getItem("jwt_token");
if (bootstrapToken) {
  window.axios.defaults.headers.common["Authorization"] =
    `Bearer ${bootstrapToken}`;
}

window.axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 429) {
      showAlert(
        "Muitas requisições simultâneas. Por favor, aguarde um momento.",
        "warning",
        { timer: 4000 },
      );
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 402) {
      if (window.location.pathname !== "/client/subscription") {
        window.location.href = "/client/subscription";
      }
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401) {
      localStorage.removeItem("jwt_token");
      window.location.href = "/client/login";
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export default () => ({
  userData: {},
  tenantData: {},
  userRoles: [],
  userPermissions: [],
  planData: null,

  isLoading: true,
  sidebarOpen: false,

  // Track permission status for reactive UI updates
  notificationPermission: "default",

  async initClientLayout() {
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get("auth_code");

    if (authCode) {
      try {
        const exchangeResponse = await axios.post("/api/public/exchange-code", {
          auth_code: authCode,
        });
        localStorage.setItem("jwt_token", exchangeResponse.data.access_token);
        localStorage.setItem("is_impersonating", "true");
        window.history.replaceState(null, null, window.location.pathname);
      } catch (error) {
        window.location.href = "/client/login";
        return;
      }
    }

    const token = localStorage.getItem("jwt_token");
    if (!token) {
      window.location.href = "/client/login";
      return;
    }
    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      const response = await axios.get("/api/client/auth/me");

      const tenantStatus = response.data.tenant_status;
      const isExpired = response.data.is_expired;
      const isStatusBlocked =
        tenantStatus === "PENDING" ||
        tenantStatus === "OVERDUE" ||
        tenantStatus === "SUSPENDED";

      if (isStatusBlocked || isExpired) {
        if (window.location.pathname !== "/client/subscription") {
          window.location.href = "/client/subscription";
          return;
        }
      }

      this.userData = response.data.user;
      this.tenantData = response.data.tenant;
      this.userPermissions = response.data.permissions;
      this.userRoles = response.data.roles || [];
      this.planData = response.data.plan;

      Sentry.setUser({
        id: String(this.userData.id),
        email: this.userData.email,
      });

      Sentry.setTag("tenant_id", String(this.tenantData.id));
      Sentry.setTag("plan", this.planData?.name || "No Plan");

      // ==========================================
      // INITIALIZE NOTIFICATIONS SAFELY
      // ==========================================
      this.initializeNotifications();
      this.listenForLiveUpdates();

      this.isLoading = false;
    } catch (error) {
      if (error.response && [401, 402].includes(error.response.status)) return;
      this.isLoading = false;
    }
  },

  initializeNotifications() {
    if (!("Notification" in window)) return;

    this.notificationPermission = Notification.permission;

    // Only register automatically if permission was already granted previously
    if (this.notificationPermission === "granted") {
      this.registerDeviceForNotifications();
    }
  },

  async promptForNotificationPermission() {
    if (!("Notification" in window)) {
      showAlert("Seu navegador não suporta notificações.", "warning");
      return;
    }

    try {
      // This MUST be called as a direct result of a user click
      const permission = await Notification.requestPermission();
      this.notificationPermission = permission;

      if (permission === "granted") {
        await this.registerDeviceForNotifications();
        showAlert("Notificações ativadas com sucesso!", "success");
      } else {
        showAlert("Permissão para notificações foi negada.", "info");
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
    }
  },

  async registerDeviceForNotifications() {
    const token = await requestFcmToken();

    if (token) {
      try {
        await axios.post("/api/client/device-tokens", {
          token: token,
          device_info: navigator.userAgent,
        });
      } catch (error) {
        console.error("Failed to register device token on backend", error);
      }
    }
  },

  listenForLiveUpdates() {
    onForegroundMessage((payload) => {
      console.log("Foreground message received:", payload);
      showAlert(payload.notification.body, "info");
    });
  },

  hasPermission(permission) {
    return this.userPermissions.includes(permission);
  },

  hasPlanFeature(feature) {
    return this.planData?.features?.includes(feature) || false;
  },

  hasRole(roleName) {
    return this.userRoles.includes(roleName);
  },

  async logout() {
    try {
      await axios.post("/api/client/auth/logout");
    } catch (error) {
      console.warn("Logout failed, clearing session.");
    } finally {
      Sentry.setUser(null);
      localStorage.removeItem("jwt_token");
      localStorage.removeItem("is_impersonating");
      window.location.href = "/client/login";
    }
  },
});
